/**
 * Step 1 — "Tell us about your crew": company name, team size, and role.
 *
 * Values live in the flow-level SelfServiceFlowProvider (sessionStorage-
 * backed) so they survive navigating between steps. Continue validates the
 * required fields — company name, team size, and role are all required by
 * the confirm endpoint — then advances the wizard.
 */
import { useCallback, useMemo, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { HintText } from '@/lib/base/forms';
import { FormProvider, useFormContext } from '@/lib/base/forms/form-context';
import { FormField } from '@/lib/base/forms/form-field';
import { Input } from '@/lib/base/forms/input/input';
import { RadioCard, RadioGroup } from '@/lib/base/radio-buttons';
import type { TeamSize } from '@/lib/self-service/constants';
import { TEAM_SIZE_OPTIONS } from '@/lib/self-service/constants';
import { useSelfServiceFlowState } from '@/lib/self-service/flowState';

interface CrewStepFormProps {
  companyName: string;
  onCompanyNameChange: (value: string) => void;
  teamSize: TeamSize | null;
  onTeamSizeChange: (value: TeamSize) => void;
  role: string;
  onRoleChange: (value: string) => void;
  onContinue: () => void;
}

function CrewStepForm({
  companyName,
  onCompanyNameChange,
  teamSize,
  onTeamSizeChange,
  role,
  onRoleChange,
  onContinue,
}: CrewStepFormProps) {
  const { touchAll, isValid, errorSummary, scrollToField } = useFormContext();
  // Team size is a radio group, outside the FormProvider's field registry, so
  // its "required" state is tracked here and surfaced on the first Continue.
  const [teamSizeTouched, setTeamSizeTouched] = useState(false);
  const teamSizeMissing = teamSizeTouched && teamSize === null;

  // RadioGroup is string-typed; every value it emits comes from
  // TEAM_SIZE_OPTIONS, so the narrowing cast is safe.
  const handleTeamSizeChange = useCallback(
    (value: string) => onTeamSizeChange(value as TeamSize),
    [onTeamSizeChange]
  );

  const handleContinue = useCallback(() => {
    touchAll();
    setTeamSizeTouched(true);
    if (!isValid || teamSize === null) {
      if (errorSummary.length > 0) {
        requestAnimationFrame(() => scrollToField(errorSummary[0].name));
      }
      return;
    }
    onContinue();
  }, [touchAll, isValid, teamSize, errorSummary, scrollToField, onContinue]);

  return (
    <div className='flex flex-col gap-6'>
      <FormField name='companyName' label='Company name' isRequired>
        {({ inputId, labelId, isInvalid, showError }) => (
          <Input
            id={inputId}
            aria-labelledby={labelId}
            className='w-full'
            placeholder='Company name'
            value={companyName}
            onChange={onCompanyNameChange}
            name='companyName'
            isRequired
            isInvalid={isInvalid}
            showError={showError}
            tooltip="We'll use this to name your workspace."
            autoFocus
          />
        )}
      </FormField>

      <div className='flex flex-col gap-2'>
        <span className='text-sm font-semibold text-[var(--color-text-primary)]'>
          Team size
        </span>
        <RadioGroup
          value={teamSize}
          onChange={handleTeamSizeChange}
          aria-label='Team size'
          className='grid grid-cols-2 gap-3 sm:grid-cols-4'
        >
          {TEAM_SIZE_OPTIONS.map(option => (
            <RadioCard key={option} value={option} variant='button'>
              {option}
            </RadioCard>
          ))}
        </RadioGroup>
        {teamSizeMissing && (
          <HintText isInvalid showError>
            Pick a team size.
          </HintText>
        )}
      </div>

      <FormField name='role' label='Your role' isRequired>
        {({ inputId, labelId, isInvalid, showError }) => (
          <Input
            id={inputId}
            aria-labelledby={labelId}
            className='w-full'
            placeholder='Founder'
            value={role}
            onChange={onRoleChange}
            name='role'
            isRequired
            isInvalid={isInvalid}
            showError={showError}
            tooltip='Helps Ahoy tailor your workspace to how you work.'
          />
        )}
      </FormField>

      <Button size='lg' className='w-full' onClick={handleContinue}>
        Continue
      </Button>
    </div>
  );
}

interface CrewStepProps {
  onContinue: () => void;
}

export function CrewStep({ onContinue }: CrewStepProps) {
  const { state, updateState } = useSelfServiceFlowState();

  const handleCompanyNameChange = useCallback(
    (companyName: string) => updateState({ companyName }),
    [updateState]
  );
  const handleTeamSizeChange = useCallback(
    (teamSize: TeamSize) => updateState({ teamSize }),
    [updateState]
  );
  const handleRoleChange = useCallback(
    (role: string) => updateState({ role }),
    [updateState]
  );

  const fields = useMemo(
    () => ({ companyName: { required: true }, role: { required: true } }),
    []
  );
  const values = useMemo(
    () => ({ companyName: state.companyName, role: state.role }),
    [state.companyName, state.role]
  );

  return (
    <FormProvider fields={fields} values={values}>
      <CrewStepForm
        companyName={state.companyName}
        onCompanyNameChange={handleCompanyNameChange}
        teamSize={state.teamSize}
        onTeamSizeChange={handleTeamSizeChange}
        role={state.role}
        onRoleChange={handleRoleChange}
        onContinue={onContinue}
      />
    </FormProvider>
  );
}
